import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { MdOutlineInventory2 } from "react-icons/md";
import StatusIcons from "../../../../Components/StatusIcons/StatusIcons";

import { fetchShipmentPackagesById } from "../../../../services/ShipperServices/Shipments/shipments.services";

const ShipmentPackagesSkeleton = () => (
  <div className="space-y-1.5 py-1">
    {[1, 2, 3].map((item) => (
      <div
        key={item}
        className="flex items-center gap-2 rounded-lg bg-ui-page px-2 py-1.5"
      >
        <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-ui-border" />

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="h-2.5 w-2/3 animate-pulse rounded bg-ui-border" />
          <div className="h-2 w-1/2 animate-pulse rounded bg-ui-border-subtle" />
        </div>

        <div className="h-4 w-12 shrink-0 animate-pulse rounded bg-ui-border" />
      </div>
    ))}
  </div>
);

const PackageRow = ({ pkg, index, weightUnit, dimensionUnit }) => {
  const description = pkg?.description || `Package ${index + 1}`;

  const weight = pkg?.weight ?? "-";

  const length = pkg?.length;
  const width = pkg?.width;
  const height = pkg?.height;

  const hasDimensions = length != null && width != null && height != null;

  const parcelId = pkg?.parcel_id;

  return (
    <div className="flex items-center gap-2 rounded-lg bg-ui-page px-2 py-1.5 shadow-ui-sm transition-colors duration-150 hover:bg-ui-row-hover">
      {/* Package Number */}
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-ui-border text-[9px] font-semibold leading-none text-ui-text-muted">
        #{index + 1}
      </div>

      {/* Package Information */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="mb-0 truncate text-[11px] font-medium leading-[1.25] text-ui-text">
          {description}
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[9px] leading-none text-ui-text-muted">
          <span>
            {weight} {weightUnit}
          </span>

          {hasDimensions && (
            <span>
              {length}×{width}×{height} {dimensionUnit}
            </span>
          )}

          {parcelId && (
            <span className="max-w-[180px] truncate">{parcelId}</span>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="flex h-6 w-auto shrink-0 items-center justify-center">
        <StatusIcons status={pkg?.tracking_status} />
      </div>
    </div>
  );
};

const ShipmentPackagesModal = ({
  open,
  onClose,
  shipmentId,
  shipmentTracking,
  unit,
}) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const isImperial = unit === "Imperial";

  const weightUnit = isImperial ? "lb" : "kg";
  const dimensionUnit = isImperial ? "in" : "cm";

  useEffect(() => {
    if (!open || !shipmentId) return;

    let active = true;

    const fetchPackages = async () => {
      try {
        setLoading(true);
        setPackages([]);

        const response = await fetchShipmentPackagesById(shipmentId);

        if (!active) return;

        const shipmentPackages = response?.data?.result ?? [];

        setPackages(shipmentPackages);
      } catch (error) {
        if (!active) return;

        console.error("Failed to fetch shipment packages:", error);

        setPackages([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPackages();

    return () => {
      active = false;
    };
  }, [open, shipmentId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "rounded-md bg-ui-surface",
        sx: {
          backgroundColor: "var(--color-ui-surface)",
        },
      }}
    >
      <div className="flex max-h-[70vh] flex-col bg-ui-surface">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-ui-border bg-ui-surface px-3.5 py-2.5">
          <div className="min-w-0">
            <h2 className="mb-0 truncate pt-2 text-[13px] font-semibold leading-tight text-ui-text">
              Shipment Packages{" "}
              <span className="font-normal">({shipmentTracking || "-"})</span>
            </h2>

            <p className="mb-0 mt-0.5 text-[10px] leading-tight text-ui-text-muted">
              {loading
                ? "Loading packages..."
                : `${packages.length} ${
                    packages.length === 1 ? "package" : "packages"
                  }`}
            </p>
          </div>

          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="group flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-brand-light bg-ui-surface transition-all duration-150 hover:!bg-brand"
          >
            <RxCross2 className="h-3 w-3 text-ui-text-muted transition-colors duration-150 group-hover:text-white" />
          </button>
        </div>

        {/* Packages */}
        <div className="min-h-[180px] overflow-y-auto bg-ui-surface px-2.5 py-1.5">
          {loading ? (
            <div className="px-1.5">
              <ShipmentPackagesSkeleton />
            </div>
          ) : packages.length > 0 ? (
            <div>
              {/* Section Header */}
              <div className="mb-1.5 flex items-center gap-1.5 px-1.5 py-1.5">
                <MdOutlineInventory2 className="h-3 w-3 text-ui-text-subtle" />

                <span className="text-[10px] font-semibold uppercase leading-none tracking-wide text-ui-text-muted">
                  Packages
                </span>

                <span className="text-[10px] leading-none text-ui-text-subtle">
                  ({packages.length})
                </span>
              </div>

              {/* Package List */}
              <div className="space-y-1.5">
                {packages.map((pkg, index) => (
                  <PackageRow
                    key={pkg?.id ?? index}
                    pkg={pkg}
                    index={index}
                    weightUnit={weightUnit}
                    dimensionUnit={dimensionUnit}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[160px] items-center justify-center">
              <p className="mb-0 text-xs text-ui-text-muted">
                No packages found.
              </p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ShipmentPackagesModal;
